import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import AIContentBlock from "@/components/AIContentBlock";

const Group = ({ title, items, render }) =>
  items?.length > 0 && (
    <div className="mt-8">
      <h2 className="text-xs font-black uppercase tracking-[0.25em] text-slate-500 mb-3">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{items.map(render)}</div>
    </div>
  );

const Card = ({ to, img, title, sub, testId }) => (
  <Link to={to} data-testid={testId} className="card-lift bg-white rounded-2xl border border-slate-100 p-3 flex items-center gap-3">
    <img src={img} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
    <div className="min-w-0">
      <p className="font-bold text-sm text-slate-900 truncate">{title}</p>
      <p className="text-xs text-slate-500 truncate">{sub}</p>
    </div>
  </Link>
);

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!q) return;
    setResults(null);
    api.get("/search", { params: { q } }).then((r) => setResults(r.data)).catch(console.error);
  }, [q]);

  const total = results
    ? ["countries", "artists", "genres", "instruments", "events"].reduce((n, k) => n + (results[k]?.length || 0), 0)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="search-page">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500 mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> Search</p>
      <h1 className="font-display text-3xl sm:text-4xl font-black text-slate-900">
        Results for "<span className="gradient-text">{q}</span>"
      </h1>
      {results && <p className="text-sm text-slate-500 mt-2" data-testid="search-count">{total} result{total !== 1 && "s"} found</p>}

      <AIContentBlock pageKey="search" topic="Searching the world's music — how to discover artists, genres, instruments and places on MusicPulse" compact />

      {!results && q && <div className="mt-16 flex justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>}

      {results && (
        <>
          <Group title="Countries & Places" items={results.countries} render={(c) => <Card key={c.slug} to={`/places/${c.slug}`} img={c.image} title={`${c.flag} ${c.name}`} sub={c.continent} testId={`search-country-${c.slug}`} />} />
          <Group title="Artists & Creators" items={results.artists} render={(a) => <Card key={a.slug} to={`/artists/${a.slug}`} img={a.image} title={a.name} sub={`${a.roles?.[0]} · ${a.country}`} testId={`search-artist-${a.slug}`} />} />
          <Group title="Genres & Traditions" items={results.genres} render={(g) => <Card key={g.slug} to={`/genres/${g.slug}`} img={g.image} title={g.name} sub={g.origin_country} testId={`search-genre-${g.slug}`} />} />
          <Group title="Instruments" items={results.instruments} render={(i) => <Card key={i.slug} to={`/instruments/${i.slug}`} img={i.image} title={i.name} sub={i.origin} testId={`search-instrument-${i.slug}`} />} />
          <Group title="Events" items={results.events} render={(e) => <Card key={e.slug} to={`/events/${e.slug}`} img={e.image} title={e.title} sub={`${e.city}, ${e.country}`} testId={`search-event-${e.slug}`} />} />
          {total === 0 && <p className="mt-10 text-slate-500" data-testid="search-empty">Nothing found. Try an artist, country, genre or instrument name.</p>}
        </>
      )}
    </div>
  );
}
